import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { ExpressApiProvider } from '@/api/ExpressApiProvider.tsx';
import { App } from '@/App.tsx';
import { AuthGate } from '@/auth/AuthGate.tsx';
import { ErrorBoundary } from '@/components/ErrorBoundary.tsx';
import { RootLayout } from '@/layouts/RootLayout.tsx';
import { registerLogFlushOnPageHide } from '@/logger';
import { Providers } from '@/Providers.tsx';
import { ActivateStrategy } from '@/screens/activate-strategy/ActivateStrategy.tsx';
import { AuthInstagram } from '@/screens/auth/AuthInstagram.tsx';
import { AuthSquare } from '@/screens/auth/AuthSquare.tsx';
import { DemoRoutes } from '@/screens/demo/DemoRoutes.tsx';
import { StartCampaign } from '@/screens/start-campaign/StartCampaign.tsx';
import { initSentry } from '@/sentry-init';
import { UserSettingsProvider } from '@/spaces/settings/UserSettingsProvider.tsx';
import { installResizeObserverFilter } from '@/utils/resizeObserverFilter';

initSentry();

// After initSentry(), because the flush reports through Sentry what it cannot send.
registerLogFlushOnPageHide();

// ADO-1149: suppress benign ResizeObserver loop warnings so they don't pollute bug reports.
installResizeObserverFilter();

const hostname = window.location.hostname;
if (hostname.includes('localhost')) {
	document.title = 'Project Bridgeway [LOCAL]';
} else if (hostname.includes('stage')) {
	document.title = 'Project Bridgeway [STAGE]';
} else if (hostname.includes('dev')) {
	document.title = 'Project Bridgeway [DEV]';
}

createRoot(document.getElementById('root')!).render(
	<StrictMode>
		<Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
			<Providers>
				<RootLayout>
					<ErrorBoundary>
						<Router
							future={{
								v7_startTransition: true,
								v7_relativeSplatPath: true,
							}}
						>
							<Routes>
								<Route path="/demo/*" element={<DemoRoutes />} />
								{/* Linked from a text message, so it is opened on a phone.
								    It needs the real IMS sign-in but NOT the desktop gate. */}
								{/* Turning a strategy on is the owner's commitment to the
								    plan. Linked from a text, so opened on a phone. */}
								<Route
									path="/activate-strategy/:strategyId"
									element={
										<AuthGate allowMobile allowWithoutUserState>
											<ActivateStrategy />
										</AuthGate>
									}
								/>
								<Route
									path="/start-campaign/:strategyId/:campaignId"
									element={
										<AuthGate allowMobile allowWithoutUserState>
											<StartCampaign />
										</AuthGate>
									}
								/>
								<Route
									path="/auth/instagram"
									element={
										<UserSettingsProvider>
											<AuthInstagram />
										</UserSettingsProvider>
									}
								/>
								<Route
									path="/auth/square"
									element={
										<UserSettingsProvider>
											<AuthSquare />
										</UserSettingsProvider>
									}
								/>
								{[
									'/',
									'/campaigns',
									'/brand',
									'/content',
									'/content/:artifactId',
									'/contacts',
									'/audiences',
									'/settings',
									'/chat',
									'/chat/:chatId',
									'*',
								].map((path) => (
									<Route
										key={path}
										path={path}
										element={
											<AuthGate>
												<UserSettingsProvider>
													<ExpressApiProvider>
														<App />
													</ExpressApiProvider>
												</UserSettingsProvider>
											</AuthGate>
										}
									/>
								))}
							</Routes>
						</Router>
					</ErrorBoundary>
				</RootLayout>
			</Providers>
		</Sentry.ErrorBoundary>
	</StrictMode>,
);
